// src/modules/app-views/ticket-events/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import TicketEventsInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import ticketEventsInitActionButton from './pages/Init/actions/actionButton';
import ticketEventsInitMenuButton from './pages/Init/actions/menuButton';
import DocumentProvider from 'components/core/DocumentProvider';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import TicketEventsDocument from './pages/Document';
import ticketEventsDocumentActionButton from './pages/Document/actions/actionButton';
import ticketEventsDocumentMenuButton from './pages/Document/actions/menuButton';
import ticketEventsDocumentDecorateRequest from './pages/Document/decorators/request';
import ticketEventsDocumentDecorateResponseDoc from './pages/Document/decorators/responseDoc';
import TicketEventsDocumentCardHeaderColumn2 from './pages/Document/components/TicketEventsDocumentCardHeaderColumn2';
import TicketEventsDocumentCardHeaderColumn3 from './pages/Document/components/TicketEventsDocumentCardHeaderColumn3';
import CardHeaderEventTags from './pages/Document/components/CardHeaderEventTags';
import CardHeaderEventIcons from './pages/Document/components/CardHeaderEventIcons';
import CardHeaderColumn from 'layouts/Pages/PageHeader/CardHeaderColumn';
import { createdAtItem, documentCodeItem, updatedAtItem } from 'layouts/Pages/PageHeader/CardHeaderColumn/items';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'ticketEvents';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 1,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: null,
  parametersSeed: null,

  migratory: {
    title: 'ticket_events_migration',
    schema: [],
  },
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    // -----------------------------------------------------------------------
    // PAGE CORE
    // -----------------------------------------------------------------------
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <TicketEventsInit />
      </ListProvider>
    ),

    // -----------------------------------------------------------------------
    // TOUR
    // -----------------------------------------------------------------------
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: { br: null, us: null },
    },

    // -----------------------------------------------------------------------
    // API & DATA
    // -----------------------------------------------------------------------
    api: {
      url: API.TICKET_EVENTS.PAGINATION,
      params: null,
      query: null,
      returnAsObject: false,
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // -----------------------------------------------------------------------
    // DECORATORS
    // -----------------------------------------------------------------------
    decorators: {
      request: null,
      responseDoc: null,
      responseData: null,
    },

    // -----------------------------------------------------------------------
    // ROUTING & NAVIGATION
    // -----------------------------------------------------------------------
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // -----------------------------------------------------------------------
    // PAGINATION CONFIGURATION
    // -----------------------------------------------------------------------
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [{ field: '_metadata.audit.updatedAt', order: 'desc', index: 1 }],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'inbox' },
    },

    // -----------------------------------------------------------------------
    // TABLE SETTINGS
    // -----------------------------------------------------------------------
    table: {
      actions: ['edit', 'archive', 'remove'],
      hiddenColumns: ['line', 'documentCode', 'status', 'createdAt'],
    },

    settings: null,

    // -----------------------------------------------------------------------
    // HEADER CONFIGURATION
    // -----------------------------------------------------------------------
    header: {
      visible: true,
      style: 'default',
      actions: ['filter', 'refresh', ticketEventsInitActionButton, ticketEventsInitMenuButton],
      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },
    },
  },

  document: {
    highSecurity: false,

    // -----------------------------------------------------------------------
    // PAGE CORE
    // -----------------------------------------------------------------------
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <TicketEventsDocument />
      </DocumentProvider>
    ),

    // -----------------------------------------------------------------------
    // TOUR
    // -----------------------------------------------------------------------
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: { br: null, us: null },
    },

    // -----------------------------------------------------------------------
    // API & DATA
    // -----------------------------------------------------------------------
    api: {
      url: API.TICKET_EVENTS.URL,
      params: null,
      query: null,
      returnAsObject: true,
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // -----------------------------------------------------------------------
    // DECORATORS
    // -----------------------------------------------------------------------
    decorators: {
      request: ticketEventsDocumentDecorateRequest,
      responseDoc: ticketEventsDocumentDecorateResponseDoc,
    },

    // -----------------------------------------------------------------------
    // ROUTING & NAVIGATION
    // -----------------------------------------------------------------------
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    pagination: null,
    table: null,

    // -----------------------------------------------------------------------
    // DOCUMENT SETTINGS
    // -----------------------------------------------------------------------
    settings: {
      initialTab: 'record',
      initialValues: {
        status: 'draft',
        pricingConfig: {
          demandWeight: 0.4,
          scarcityWeight: 0.4,
          velocityWeight: 0.2,
          recalcIntervalHours: 1,
          criticalWindowHours: 72,
          salesCurve: [],
        },
      },
      actions: [
        'editRefresh',
        ticketEventsDocumentActionButton(),
        ticketEventsDocumentMenuButton(),
        'return',
      ],
      viewMode: 'auto',
      backPage: 'init',
    },

    // -----------------------------------------------------------------------
    // HEADER CONFIGURATION
    // -----------------------------------------------------------------------
    header: {
      visible: true,
      style: 'card',

      avatar: {
        visible: false,
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
      },

      card: {
        title: props => {
          const name = props?.name;
          return <CardHeaderTitle {...props} name={name?.toUpperCase()} />;
        },
        subtitle: props => {
          const description = props?.description;
          return description ? <span className="text-muted">{description}</span> : null;
        },

        column1: props => (
          <CardHeaderColumn {...props} items={[documentCodeItem, createdAtItem, updatedAtItem]} />
        ),

        column2: props => <TicketEventsDocumentCardHeaderColumn2 {...props} />,
        column3: props => <TicketEventsDocumentCardHeaderColumn3 {...props} />,

        tags: data => <CardHeaderEventTags data={data} />,
        icons: data => <CardHeaderEventIcons data={data} />,
      },
    },
  },
};

// *******************************************************************************
// MOCK DATA
// *******************************************************************************
const MOCK = {
  document: {
    name: 'Show Rock 2026',
    totalTickets: 1000,
    minimumPrice: 50,
    maximumPrice: 250,
    status: 'draft',
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, MOCK, preConfigChanges };
